'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useIsMobile } from '@repo/ui/hooks';
import { DEFAULT_SIDEBAR, persistDefaultSidebarState, SIDEBAR_KEYBOARD_SHORTCUT, SIDEBAR_REGISTRY, SIDEBAR_TOKEN } from '../context';
import type { SidebarContextProps, SidebarControlledState, SidebarRegistration, SidebarRegistry, SidebarRegistryToken, SidebarSetOpenOptions, SidebarState, SidebarToken } from '../context';
const EMPTY_CONTROLLED_SIDEBARS: readonly never[] = [];
type UseSidebarProviderStateOptions<Registry extends SidebarRegistry = SidebarRegistry> = {
  /**
   * Controls named sidebar visibility on desktop and mobile. On mobile, the first open entry is
   * the active Sheet and opening another entry closes the current one through onOpenChange.
   */
  controlledSidebars?: readonly SidebarControlledState<SidebarRegistryToken<Registry>>[];
  defaultOpen?: boolean;
  onOpenChange?: (_open: boolean) => void;
  open?: boolean;
  /**
   * Keep this object stable after mount. Prefer a module-scope createSidebarRegistry() call.
   */
  registry?: Registry;
};
function useSidebarProviderState(t0) {
  const $ = _c(40);
  const {
    controlledSidebars: t1,
    defaultOpen: t2,
    onOpenChange: setOpenProp,
    open: openProp,
    registry
  } = t0;
  const controlledSidebars = t1 === undefined ? EMPTY_CONTROLLED_SIDEBARS : t1;
  const defaultOpen = t2 === undefined ? true : t2;
  const isMobile = useIsMobile();
  const [initialRegistry] = React.useState(registry);
  if (registry !== initialRegistry) {
    throw new Error("SidebarProvider registry cannot change after mount. Create the registry at module scope or otherwise keep the same registry object for the provider lifetime.");
  }
  let t3;
  if ($[0] !== defaultOpen || $[1] !== initialRegistry) {
    t3 = () => {
      const initialOpen = new Map([[DEFAULT_SIDEBAR, defaultOpen]]);
      if (initialRegistry) {
        Object.values(initialRegistry).forEach(target => {
          initialOpen.set(target, target[SIDEBAR_TOKEN].defaultOpen);
        });
      }
      return initialOpen;
    };
    $[0] = defaultOpen;
    $[1] = initialRegistry;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [desktopOpen, setDesktopOpen] = React.useState(t3);
  const [mobileOpen, setMobileOpen] = React.useState(null);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map();
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const mountedSidebars = React.useRef(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Map();
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [sidebarRegistrations, setSidebarRegistrations] = React.useState(t5);
  const controlledMobileTarget = controlledSidebars.find(_temp)?.target ?? null;
  const [previousControlledMobileTarget, setPreviousControlledMobileTarget] = React.useState(controlledMobileTarget);
  if (controlledMobileTarget !== previousControlledMobileTarget) {
    setPreviousControlledMobileTarget(controlledMobileTarget);
    if (controlledMobileTarget) {
      setMobileOpen(null);
    }
  }
  let t6;
  if ($[5] !== initialRegistry) {
    t6 = target_0 => {
      if (target_0 === DEFAULT_SIDEBAR) {
        return;
      }
      if (!initialRegistry || target_0[SIDEBAR_TOKEN].registry !== initialRegistry[SIDEBAR_REGISTRY]) {
        throw new Error(`Sidebar "${target_0.name}" does not belong to the registry passed to this SidebarProvider.`);
      }
    };
    $[5] = initialRegistry;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const assertRegisteredTarget = t6;
  let t7;
  if ($[7] !== controlledSidebars || $[8] !== desktopOpen || $[9] !== openProp) {
    t7 = target_1 => {
      if (target_1 === DEFAULT_SIDEBAR && openProp !== undefined) {
        return openProp;
      }
      const controlledState = controlledSidebars.find(state_0 => state_0.target === target_1);
      return controlledState?.open ?? desktopOpen.get(target_1) ?? target_1[SIDEBAR_TOKEN].defaultOpen;
    };
    $[7] = controlledSidebars;
    $[8] = desktopOpen;
    $[9] = openProp;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const getDesktopOpen = t7;
  let t8;
  if ($[11] !== assertRegisteredTarget || $[12] !== controlledSidebars || $[13] !== setOpenProp) {
    t8 = (target_2, open, options) => {
      assertRegisteredTarget(target_2);
      if (target_2 === DEFAULT_SIDEBAR) {
        if (setOpenProp) {
          setOpenProp(open);
        } else {
          setDesktopOpen(current => new Map(current).set(target_2, open));
        }
        if (options?.persist !== false) {
          persistDefaultSidebarState(open);
        }
        return;
      }
      const controlledState_0 = controlledSidebars.find(state_1 => state_1.target === target_2);
      if (controlledState_0) {
        controlledState_0.onOpenChange(open);
      } else {
        setDesktopOpen(current_0 => new Map(current_0).set(target_2, open));
      }
    };
    $[11] = assertRegisteredTarget;
    $[12] = controlledSidebars;
    $[13] = setOpenProp;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const setSidebarOpen = t8;
  let t9;
  if ($[15] !== assertRegisteredTarget || $[16] !== controlledSidebars) {
    t9 = (target_3, open_0) => {
      assertRegisteredTarget(target_3);
      const controlledState_1 = controlledSidebars.find(state_2 => state_2.target === target_3);
      if (controlledState_1) {
        if (open_0) {
          setMobileOpen(null);
          controlledSidebars.forEach(state_3 => {
            if (state_3.target !== target_3 && state_3.open) {
              state_3.onOpenChange(false);
            }
          });
          if (!controlledState_1.open) {
            controlledState_1.onOpenChange(true);
          }
        } else {
          if (controlledState_1.open) {
            controlledState_1.onOpenChange(false);
          }
        }
        return;
      }
      setMobileOpen(current_1 => {
        if (open_0) {
          return target_3;
        }
        return current_1 === target_3 ? null : current_1;
      });
      if (open_0) {
        controlledSidebars.forEach(_temp2);
      }
    };
    $[15] = assertRegisteredTarget;
    $[16] = controlledSidebars;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const setSidebarOpenMobile = t9;
  let t10;
  if ($[18] !== getDesktopOpen || $[19] !== sidebarRegistrations) {
    t10 = target_4 => {
      const registration = sidebarRegistrations.get(target_4);
      if (!registration) {
        return "0px";
      }
      const outerWidths = [];
      for (const [other, otherRegistration] of sidebarRegistrations) {
        if (other === target_4) {
          break;
        }
        if (otherRegistration.side === registration.side && getDesktopOpen(other)) {
          outerWidths.push(otherRegistration.width);
        }
      }
      return outerWidths.length > 0 ? `calc(${outerWidths.join(" + ")})` : "0px";
    };
    $[18] = getDesktopOpen;
    $[19] = sidebarRegistrations;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const getStackOffset = t10;
  let t11;
  if ($[21] !== assertRegisteredTarget || $[22] !== controlledMobileTarget || $[23] !== getDesktopOpen || $[24] !== getStackOffset || $[25] !== isMobile || $[26] !== mobileOpen || $[27] !== setSidebarOpen || $[28] !== setSidebarOpenMobile) {
    t11 = target_5 => {
      assertRegisteredTarget(target_5);
      const open_1 = getDesktopOpen(target_5);
      const openMobile = (controlledMobileTarget ?? mobileOpen) === target_5;
      const stackOffset = getStackOffset(target_5);
      const setOpen = (nextOpen, options_0) => setSidebarOpen(target_5, nextOpen, options_0);
      const setOpenMobile = nextOpen_0 => setSidebarOpenMobile(target_5, nextOpen_0);
      const toggleSidebar = () => {
        if (isMobile) {
          setSidebarOpenMobile(target_5, !openMobile);
        } else {
          setSidebarOpen(target_5, !open_1);
        }
      };
      return {
        isMobile,
        open: open_1,
        openMobile,
        setOpen,
        setOpenMobile,
        stackOffset,
        state: open_1 ? "expanded" : "collapsed",
        toggleSidebar
      };
    };
    $[21] = assertRegisteredTarget;
    $[22] = controlledMobileTarget;
    $[23] = getDesktopOpen;
    $[24] = getStackOffset;
    $[25] = isMobile;
    $[26] = mobileOpen;
    $[27] = setSidebarOpen;
    $[28] = setSidebarOpenMobile;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const getSidebarState = t11;
  let t12;
  if ($[30] !== assertRegisteredTarget) {
    t12 = (target_6, registration_0) => {
      assertRegisteredTarget(target_6);
      const mountedCount = mountedSidebars.current.get(target_6) ?? 0;
      if (process.env.NODE_ENV !== "production" && mountedCount > 0) {
        throw new Error(`Sidebar "${target_6.name}" is mounted more than once in the same SidebarProvider. Sidebar names must be unique within a provider.`);
      }
      mountedSidebars.current.set(target_6, mountedCount + 1);
      setSidebarRegistrations(current_2 => new Map(current_2).set(target_6, registration_0));
      return () => {
        const currentCount = mountedSidebars.current.get(target_6) ?? 1;
        if (currentCount === 1) {
          mountedSidebars.current.delete(target_6);
        } else {
          mountedSidebars.current.set(target_6, currentCount - 1);
        }
        setSidebarRegistrations(current_3 => {
          const next = new Map(current_3);
          next.delete(target_6);
          return next;
        });
      };
    };
    $[30] = assertRegisteredTarget;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const registerSidebar = t12;
  let t13;
  if ($[32] !== getSidebarState) {
    t13 = () => {
      const defaultState = getSidebarState(DEFAULT_SIDEBAR);
      defaultState.toggleSidebar();
    };
    $[32] = getSidebarState;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const toggleDefaultSidebar = t13;
  let t14;
  let t15;
  if ($[34] !== toggleDefaultSidebar) {
    t14 = () => {
      const handleKeyDown = event => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleDefaultSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t15 = [toggleDefaultSidebar];
    $[34] = toggleDefaultSidebar;
    $[35] = t14;
    $[36] = t15;
  } else {
    t14 = $[35];
    t15 = $[36];
  }
  React.useEffect(t14, t15);
  let t16;
  if ($[37] !== getSidebarState || $[38] !== registerSidebar) {
    t16 = {
      getSidebarState,
      registerSidebar
    };
    $[37] = getSidebarState;
    $[38] = registerSidebar;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp2(state_4) {
  if (state_4.open) {
    state_4.onOpenChange(false);
  }
}
function _temp(state) {
  return state.open;
}
export { useSidebarProviderState };
export type { UseSidebarProviderStateOptions };